/**
 * The hero piece: the top of the home page. The studio's About photo full
 * bleed behind its name (or the page ground when it has none), the place and
 * year line, the name, the tagline, buttons to what the studio offers and a
 * row of facts, each only where the studio has it.
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'hero',
  description:
    "The top of the home page: the studio's name over its About photo, its place and year, its tagline, buttons to its services and events, and a few facts.",
  options: {
    showPhoto: { type: 'boolean', default: true, description: "Draw the studio's About photo behind the name.", showsPart: 'photo' },
    showButtons: { type: 'boolean', default: true, description: 'Show the buttons to the services and the events.', showsPart: 'buttons' },
    showFacts: { type: 'boolean', default: true, description: 'Show the row of facts at the foot of the hero.', showsPart: 'facts' },
    servicesLabel: { type: 'string', default: 'Book a session', maxLength: 40, description: 'The words on the button to the services.' },
    eventsLabel: { type: 'string', default: 'See events', maxLength: 40, description: 'The words on the button to the events.' },
  },
  parts: {
    section: { description: 'The whole hero.', required: false },
    photo: { description: "The studio's About photo.", required: false, role: 'photo' },
    eyebrow: { description: "The line with the studio's place and year.", required: false },
    name: { description: "The studio's name.", required: false, role: 'title' },
    tagline: { description: "The studio's line under its name.", required: false },
    buttons: { description: 'The buttons to the services and events.', required: false },
    servicesButton: { description: 'The button to the services.', required: false, role: 'primary-action' },
    eventsButton: { description: 'The button to the events.', required: false, role: 'secondary-action' },
    facts: { description: 'The row of facts.', required: false },
    fact: { description: 'One fact: its label, value and note.', required: false },
    factLabel: { description: "A fact's label.", required: false },
    factValue: { description: "A fact's value; the next opening's opens that time.", required: false },
    factNote: { description: "A fact's small second line.", required: false },
  },
  actions: {
    openServices: { description: "Scrolls to the home's services.", args: [] },
    openEvents: { description: 'Opens the events page.', args: [] },
    openFact: { description: "Opens what a fact links to (the next opening's service page with that time picked).", args: [{ name: 'factId', kind: 'item', list: 'facts', key: 'id' }] },
  },
  events: {
    navigated: 'A visitor followed a hero button or fact.',
  },
});

/** Which fact this is. */
export type HeroFactId = 'nextOpening' | 'length' | 'format';

export interface HeroFact {
  /** Which fact this is. */
  readonly id: HeroFactId;
  /** The label above the value: Next opening, Sessions, Format. */
  readonly label: string;
  /** The value: "Today, 11:15 AM", "30 to 90 min", "In person". */
  readonly value: string;
  /** The small line under the value, or null: the service's name, "Five services", "and online". */
  readonly note: string | null;
  /** Where the value links, or null when it is plain text. */
  readonly href: string | null;
}

/** What the hero knows. */
export interface HeroState {
  /** loading until the studio's profile is known; error when it failed. */
  readonly status: 'loading' | 'ready' | 'error';
  /** The studio's name; empty while loading. */
  readonly name: string;
  /** The studio's tagline, or null. */
  readonly tagline: string | null;
  /** The studio's location and established year joined with " · ", or null when neither is set. */
  readonly eyebrow: string | null;
  /** The studio's About photo, or null to draw the hero on the page ground. */
  readonly photoUrl: string | null;
  /** True when the studio lists a service (the services button shows). */
  readonly hasServices: boolean;
  /** True when the studio has an upcoming event (the events button shows). */
  readonly hasEvents: boolean;
  /** The facts that have data, in order; empty while the services load. */
  readonly facts: readonly HeroFact[];
}

/** What a drawing can ask the hero to do. */
export interface HeroActions {
  /** Go to the home's services. */
  readonly openServices: () => void;
  /** Open the events page. */
  readonly openEvents: () => void;
  /** Open what the fact with this id links to; a fact with no link is ignored. */
  readonly openFact: (factId: HeroFactId) => void;
}

/** What the hero tells its host. */
export interface HeroEvents {
  /** A visitor followed a button or a fact. */
  readonly navigated: { readonly target: 'services' | 'events' | HeroFactId };
}
